// @flow
// Module dependencies
import P from 'app/common/lib/P';
import Pc from 'app/common/lib/Pc';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import script from 'app/common/lib/Script';
import user from 'app/common/lib/User';
import { MultiTreatmentExp } from 'app/common/lib/experiment';

/** @type {boolean} */
let _fbInitialized = false;

const Facebook = {
  /**
   * @param {function(FB)} callback Once the Facebook SDK is loaded, call this.
   * @param {!user} currentUser
   *
   */
  ensureInit(
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    callback: *,
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    currentUser: * = user,
    locale: string = 'en_US',
    unauthPageWrapperOptimizationExp?: MultiTreatmentExp,
  ) {
    const MODULE_FACEBOOK = 'fb';
    script.load([], `//connect.facebook.net/${locale}/sdk.js`, MODULE_FACEBOOK);
    script.ready(MODULE_FACEBOOK, () => {
      this._fbCallback(callback, currentUser, unauthPageWrapperOptimizationExp);
    });
    window.fb_init = PCONST.FacebookScriptLoadStatus.STARTED;
  },

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  _fbCallback(callback: *, currentUser: *, unauthPageWrapperOptimizationExp?: MultiTreatmentExp) {
    const fb = /** @type {!FB} */ (window.FB);
    // We've seen an instance of all.js 404'ing, and we're still calling the callback.
    if (!fb) {
      return;
    }

    if (!_fbInitialized) {
      if (
        unauthPageWrapperOptimizationExp &&
        (unauthPageWrapperOptimizationExp.check(
          'enabled_fb_and_prevent_google_one_tap_error_refresh',
        ) ||
          unauthPageWrapperOptimizationExp.check(
            'enabled_scroll_and_prevent_google_one_tap_error_refresh',
          ) ||
          unauthPageWrapperOptimizationExp.check(
            'enabled_load_secondary_content_after_user_scroll',
          ) ||
          unauthPageWrapperOptimizationExp.check('enabled_load_secondary_content_after_fb_success'))
      ) {
        // If we delay the unauth banner to load then this version of fb script will used for
        // the facebook connect button, thus we need to keep the version and xfbml flag
        fb.init({
          appId: Pc.FB_KEY,
          status: true,
          xfbml: true,
          version: 'v2.7',
        });
      } else {
        fb.init({
          appId: Pc.FB_KEY,
          status: true,
          version: 'v2.2',
        });
      }

      declare var FB;
      FB.Event.subscribe('auth.statusChange', (evt) => {
        Facebook.refreshAccessToken(currentUser);
      });

      _fbInitialized = true;
      window.fb_init = PCONST.FacebookScriptLoadStatus.FINISHED;
    }

    callback(fb);
  },

  /**
   * Ensures that both the Facebook JS SDK is loaded and that the Facebook user is logged in.
   * This can be used for Facebook SDK actions that require a user.
   *
   * @param {function(FB)} callback Once the Facebook SDK is loaded and the user is logged in, call this.
   * @param {!user} currentUser
   *
   */
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  ensureLoggedIn(callback, currentUser) {
    Facebook.ensureInit((fb) => {
      Facebook.logIn(fb, callback);
    }, currentUser);
  },

  // $FlowFixMe: new errors found from flow 0.96 upgrade
  logIn(fb, callback) {
    if (!fb) {
      return;
    }

    if (fb.getUserID()) {
      callback(fb);
    } else {
      fb.login(() => {
        callback(fb);
      });
    }
  },

  /**
   * Refreshes the Facebook access token if it is close to being expired.
   * @param {!user} currentUser
   */
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  refreshAccessToken(currentUser) {
    if (
      !currentUser.isAuthenticated() ||
      !currentUser.attributes.facebook_id ||
      currentUser.isLimitedLogin()
    ) {
      return;
    }

    Facebook.ensureInit((fb) => {
      if (fb.getUserID()) {
        const accessToken = fb.getAccessToken();
        if (accessToken) {
          const resource = ResourceFactory.create('UserSocialNetworkResource', {
            facebook_token: accessToken,
          });
          resource.callUpdate({ showError: false });
        }
      }
    }, currentUser);
  },

  /**
   * Inject a Facebook tracking pixel to indicate new signups from Facebook ads
   */
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  injectTrackingPixel(pixelId) {
    (function() {
      const _fbq: any = window._fbq || (window._fbq = []);
      if (!_fbq.loaded) {
        const fbds = document.createElement('script');
        fbds.async = true;
        fbds.src = '//connect.facebook.net/en_US/fbds.js';
        const s = document.getElementsByTagName('script')[0];
        (s.parentNode: any).insertBefore(fbds, s);
        _fbq.loaded = true;
      }
    })();
    window._fbq = window._fbq || [];
    window._fbq.push(['track', pixelId, { value: '0.00', currency: 'USD' }]);
  },
};

// eslint-disable-next-line pinterest/no-referencing-p-properties
export default P.expose('Facebook', Facebook);
